import React, { useEffect, useState } from "react";
import { Select, HStack, VStack, Text, Stack } from "@chakra-ui/react";

type BirthDatePickerProps = {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  minYear?: number;
  maxYear?: number;
  allowSettingAnyBirthYear?: boolean;
  direction?: "row" | "column";
  marginBottom?: string;
  disabled?: boolean;
};

const allMonthNames = [
  "jan", "feb", "mar", "apr", "maj", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

function daysInMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function monthNameToNumber(monthName: string | null): number | null {
  if (!monthName) return null;
  const idx = allMonthNames.indexOf(monthName);
  return idx === -1 ? null : idx;
}

function monthNumberToName(monthNumber: number | null): string | null {
  if (monthNumber === null) return null;
  return allMonthNames[monthNumber] ?? null;
}

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function adjustDayToMonthAndYear(
  year: number | null,
  month: string | null,
  day: number | null
): number | null {
  if (year === null || month === null || day === null) return day;

  const monthNr = monthNameToNumber(month);
  if (monthNr === null) return day;

  const maxDays = daysInMonth(year, monthNr);
  return Math.min(day, maxDays);
}


export default function BirthDatePicker({
  value = null,
  onChange,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  allowSettingAnyBirthYear = false,
  direction,
  marginBottom,
  disabled
}: BirthDatePickerProps) {

  const hardMinBirthYearConstraint = allowSettingAnyBirthYear ? 1900 : minYear;
  const hardMaxBirthYearConstraint = allowSettingAnyBirthYear ? new Date().getFullYear() : maxYear;

  const [year, setYear] = useState<number | null>(
    value?.getFullYear() ?? null
  );
  const [month, setMonth] = useState<string | null>(
    value ? monthNumberToName(value.getMonth()) : null
  );
  const [day, setDay] = useState<number | null>(
    value?.getDate() ?? null
  );

  const [daysOptions, setDaysOptions] = useState<number[]>([]);
  const [yearsOptions, setYearsOptions] = useState<number[]>([]);

  useEffect(() => {
    // Set internal state 
    // but only when not null
    if (value != null) {
      setYear(value?.getFullYear() ?? null);
      setMonth(value ? monthNumberToName(value.getMonth()) : null);
      setDay(value?.getDate() ?? null);
    }
  }, [value]);

  // generate years
  useEffect(() => {
    const years: number[] = [];
    for (let y = hardMaxBirthYearConstraint; y >= hardMinBirthYearConstraint; y--) {
      years.push(y);
    }
    setYearsOptions(years);
  }, [hardMaxBirthYearConstraint, hardMinBirthYearConstraint]);

  // update days when month/year changes
  useEffect(() => {
    if (year !== null && month !== null) {
      const monthNr = monthNameToNumber(month);
      if (monthNr !== null) {
        const days = Array.from(
          { length: daysInMonth(year, monthNr) },
          (_, i) => i + 1
        );
        setDaysOptions(days);
        return;
      }
    }
    setDaysOptions([]);
  }, [year, month]);

  const updateDate = (
    newYear: number | null,
    newMonth: string | null,
    newDay: number | null
  ) => {
    const adjustedDay = adjustDayToMonthAndYear(
      newYear,
      newMonth,
      newDay
    );

    setYear(newYear);
    setMonth(newMonth);
    setDay(adjustedDay);

    if (newYear === null || newMonth === null || adjustedDay === null) {
      onChange(null);
      return;
    }

    const monthNr = monthNameToNumber(newMonth);
    if (monthNr === null) {
      onChange(null);
      return;
    }

    onChange(new Date(newYear, monthNr, adjustedDay));
  };

  return (
    <VStack marginBottom={marginBottom}>
      <Stack direction={direction ?? "row"} width={"100%"}>
        {/* Year */}
        <Select
          background={disabled ? undefined : "white"}
          isDisabled={disabled}
          value={year ?? ""}
          onChange={(e) =>
            updateDate(
              e.target.value ? Number(e.target.value) : null,
              null,
              null
            )
          }
        >
          <option value="" disabled>
            År
          </option>
          {yearsOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>

        {/* Month */}
        <Select
          background={disabled ? undefined : "white"}
          value={month ?? ""}
          onChange={(e) =>
            updateDate(
              year,
              e.target.value || null,
              day
            )
          }
          isDisabled={year === null || disabled}
        >
          <option value="" disabled>
            Månad
          </option>
          {allMonthNames.map((m) => (
            <option key={m} value={m}>
              {capitalizeFirstLetter(m)}
            </option>
          ))}
        </Select>

        {/* Day */}
        <Select
          background={disabled ? undefined : "white"}
          value={day ?? ""}
          onChange={(e) =>
            updateDate(
              year,
              month,
              e.target.value ? Number(e.target.value) : null
            )
          }
          isDisabled={year === null || month === null || disabled}
        >
          <option value="" disabled>
            Dag
          </option>
          {daysOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>
      </Stack>
      {(allowSettingAnyBirthYear && year != null && (year > maxYear || year < minYear)) && (
        <Text color="red.500" size={"sm"}>{`Admin override (${year} är ej inom ${minYear} - ${maxYear})`}</Text>
      )}
    </VStack>
  );
}